// What the Edit panel does when the ticket changes underneath it.
//
// Pure, and separate from TicketEditPanel.jsx, for this repository's standing reason:
// there is no DOM harness, so a decision left inside a component is a decision nothing
// can check.
//
// THE FAILURE THIS PREVENTS. The panel held the ticket it was opened on as its baseline
// and diffed its draft against that. A reply that set a status, a status-only change from
// the reply box, or a linked-ticket refresh all replace the `ticket` prop while the panel
// is open; with the baseline frozen, the panel's status select kept showing the OLD status
// and Save wrote it back over the new one (changedTicketFields saw "draft differs from the
// baseline" and sent it).
//
// THE RULE. A field the agent has not edited (its draft value still equals the old
// baseline) follows the new ticket. A field the agent has edited keeps their value, even
// if the new ticket now happens to agree with it. The caller then makes `next` the
// baseline, so Save sends only what the agent changed.

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = [...new Set((a ?? []).map(String))].sort();
    const right = [...new Set((b ?? []).map(String))].sort();
    return left.length === right.length && left.every((v, i) => v === right[i]);
  }
  // `null`, `undefined` and `''` are one value in a form, as in ticket-edit.js.
  return String(a ?? '') === String(b ?? '');
};

/**
 * @param {object} saved  The baseline the draft was being compared with (flattened by
 *   `draftFromTicket`).
 * @param {object} draft  The panel's current state.
 * @param {object} next   The new ticket, flattened the same way.
 * @returns {object} A new draft: `next`'s value where the agent had not edited, the
 *   agent's own where they had.
 */
// Iterates `next`'s keys, so `saved`, `draft` and `next` must all come from draftFromTicket.
export function rebaseDraft(saved, draft, next) {
  const out = {};
  for (const field of Object.keys(next ?? {})) {
    out[field] = sameValue(saved?.[field], draft?.[field])
      ? (Array.isArray(next[field]) ? [...next[field]] : next[field])
      : draft[field];
  }
  return out;
}
